import { Directive, computed, effect, inject, input, signal, untracked } from '@angular/core';

import { NotificationService } from '@shared/services';

import { ExpressionField, FieldDefinitionsService } from '../../common';
import {
  DateToken,
  FormulaOperator,
  FormulaResult,
  FormulaToken,
  OPERATORS,
  aggregateLabel,
  formulaSnapshot,
  operatorsAround,
  parseExpression,
  withoutToken,
} from './formula';
import { validateTypedFormula } from './formula-typing';

export type FormulaFieldSlot = 'field' | 'second' | 'third';

@Directive()
export abstract class FieldFormulaBase {
  protected readonly notify = inject(NotificationService);
  private readonly fieldDefinitions = inject(FieldDefinitionsService);

  readonly moduleId = input.required<number>();
  readonly fieldId = input<number>(null);
  readonly expression = input<string>('');
  readonly isDateResult = input(false);
  readonly disabled = input(false);
  readonly fields = input<ExpressionField[]>();

  readonly loading = signal(true);
  readonly tokens = signal<FormulaToken[]>([]);
  readonly numericFields = signal<ExpressionField[]>([]);
  readonly dateFields = signal<ExpressionField[]>([]);
  readonly invalidIndexes = signal(new Set<number>());

  private readonly initialSnapshot = signal<string>(null);

  readonly changed = computed(
    () => this.initialSnapshot() !== null && formulaSnapshot(this.tokens()) !== this.initialSnapshot(),
  );
  readonly locked = computed(() => this.disabled() || this.loading());

  constructor() {
    effect(() => {
      const moduleId = this.moduleId();
      if (moduleId) {
        untracked(() => this.load());
      }
    });
  }

  load(): void {
    const fields = this.fields();
    if (fields) {
      this.applyFields(fields);
      return;
    }
    this.loading.set(true);
    this.fieldDefinitions.getExpressionFields(this.moduleId(), this.fieldId()).subscribe({
      next: fields => this.applyFields(fields),
      error: () => this.applyFields([]),
    });
  }

  result(): FormulaResult {
    const validation = validateTypedFormula(this.tokens(), this.isDateResult());
    this.invalidIndexes.set(new Set(validation.invalidIndexes));
    validation.errors.forEach(error => this.notify.error(error));
    return validation.result ?? null;
  }

  // MARK: template helpers

  functionLabel(key: number): string {
    return aggregateLabel(key);
  }

  operatorLabel(value: FormulaOperator): string {
    return OPERATORS.find(entry => entry.value === value)?.label;
  }

  operatorsAt(index: number) {
    return operatorsAround(this.tokens(), index);
  }

  fieldsFor(slot: FormulaFieldSlot, token: FormulaToken, query = ''): ExpressionField[] {
    let fields = this.dateFields();
    if (slot === 'field') {
      fields = this.numericFields();
    } else if (slot === 'second' && (token as DateToken).secondKind === 'number') {
      fields = this.numericFields().filter(field => field.isNumberFormatting);
    }
    const text = query.trim().toLowerCase();
    return text ? fields.filter(field => (field.value ?? '').toLowerCase().includes(text)) : fields;
  }

  // MARK: token edits

  setFunction(index: number, functionKey: number): void {
    this.patch(index, { functionKey });
  }

  setField(index: number, slot: FormulaFieldSlot, field: ExpressionField): void {
    if (slot === 'field') {
      this.patch(index, { field });
    } else if (slot === 'second') {
      this.patch(index, { secondField: field, useSecondField: true, secondNumber: undefined });
    } else {
      this.patch(index, { thirdField: field });
    }
  }

  setDatePart(index: number, datePart: string): void {
    this.patch(index, { datePart });
  }

  setSecondNumber(index: number, value: number): void {
    this.patch(index, { secondNumber: value, useSecondField: false, secondField: undefined });
  }

  setOperator(index: number, value: FormulaOperator): void {
    this.patch(index, { value });
  }

  setNumber(index: number, value: number): void {
    this.patch(index, { value });
  }

  // MARK: protected

  protected append(token: FormulaToken): void {
    this.insertAt(this.tokens().length, token);
  }

  protected insertAt(index: number, token: FormulaToken): void {
    const tokens = this.tokens();
    this.replace([...tokens.slice(0, index), token, ...tokens.slice(index)]);
  }

  /** Removes the token with whatever keeps the formula valid around it (see `withoutToken`). */
  protected removeAt(index: number): void {
    this.replace(withoutToken(this.tokens(), index));
  }

  /** Removes exactly one token, the way a text editor removes one character. */
  protected deleteAt(index: number): void {
    this.replace(this.tokens().filter((_, position) => position !== index));
  }

  protected replace(tokens: FormulaToken[]): void {
    this.invalidIndexes.set(new Set());
    this.tokens.set(tokens);
  }

  protected patch(index: number, changes: Partial<Record<string, unknown>>): void {
    this.invalidIndexes.update(indexes => {
      const next = new Set(indexes);
      next.delete(index);
      return next;
    });
    this.tokens.update(tokens =>
      tokens.map((token, position) => (position === index ? ({ ...token, ...changes } as FormulaToken) : token)),
    );
  }

  // MARK: private

  private applyFields(fields: ExpressionField[]): void {
    const numeric = fields.filter(field => !field.isDate);
    const date = fields.filter(field => field.isDate);
    this.numericFields.set(numeric);
    this.dateFields.set(date);
    const tokens = parseExpression(this.expression(), numeric, date);
    this.tokens.set(tokens);
    this.initialSnapshot.set(formulaSnapshot(tokens));
    this.loading.set(false);
  }
}
